import {createSelector} from 'reselect';

import {Controller} from '@stubhub/react-store-provider';
import {get} from '@stubhub/rest-method';

import dateComparator from '../../../../../../helpers/date-comparison';

function controllerFactory(namespace) {
  if (!namespace) {
    throw new Error('react-delivery-info namespace required');
  }

  const namespaceCapitalized = namespace.toUpperCase();
  const DELIVERY_INFO_LOADING = `${namespaceCapitalized}:DELIVERY_INFO_LOADING`;
  const DELIVERY_INFO_LOADED = `${namespaceCapitalized}:DELIVERY_INFO_LOADED`;
  const DELIVERY_INFO_ERROR = `${namespaceCapitalized}:DELIVERY_INFO_ERROR`;

  const fulfillmentSelector = createSelector(
    (localState) => localState.fulfillmentData,
    (fulfillmentData) => fulfillmentData || {}
  );

  /**
   *To retrieve delivery info
   *@return {isToday}, Boolean, if the EDD is Today
   *@return {deliveryMethodFromDeliveryExpectations}, Object, delivery date and deliveryMethod
   *@return {date}, Object, estimated delivery date
   *
   */
  const deliveryInfoSelector = createSelector(
    (localState) => localState.deliveryInfo,
    (deliveryInfo) => {
      if (deliveryInfo) {
        const {deliveryExpectations = []} = deliveryInfo;
        const today = new Date(Date.now());
        const [deliveryMethodFromDeliveryExpectations] = deliveryExpectations;
        const date = new Date(deliveryMethodFromDeliveryExpectations.estimatedDeliveryDateTime);
        const comparator = dateComparator(today, date);
        const isToday = comparator === 0;

        return {
          isToday,
          deliveryMethodFromDeliveryExpectations,
          date,
        };
      }
    }
  );

  const controller = new Controller({
    namespace: `${namespace}_deliveryInfo`,
    mapStateToProps(state) {
      const localState = this.getLocalState(state) || {};

      return {
        fulfillmentData: fulfillmentSelector(localState),
        ...deliveryInfoSelector(localState),
        loading: localState.loading || false,
        error: !!localState.error || false,
      };
    },
    actionCreators: {
      getDeliveryData,
      destroy,
    },
    reducers: [DELIVERY_INFO_LOADING, DELIVERY_INFO_LOADED, DELIVERY_INFO_ERROR],
  });

  controller.addResetReducer(['DELIVERY_INFO_RESET']);

  function destroy() {
    return (dispatch) => {
      dispatch({type: 'DELIVERY_INFO_RESET'});
    };
  }

  function getDeliveryData(listingId) {
    return async (dispatch, getState) => {
      try {
        const state = getState() || {};
        const {lang, gsConfig = {}} = state;
        const {shstoreId = 1} = gsConfig;
        dispatch({type: DELIVERY_INFO_LOADING, loading: true});
        const fulfillmentData = await getFulFillmentDetail(listingId, lang, shstoreId);
        const {fulfillmentWindows = []} = fulfillmentData;
        const [fulfillmentWindow] = fulfillmentWindows;
        const {deliveryMethod = {}} = fulfillmentWindow;
        const {id: deliveryMethodId} = deliveryMethod;
        const deliveryInfo = await getDeliveryExpectations(deliveryMethodId, listingId, lang, shstoreId);
        dispatch({type: DELIVERY_INFO_LOADED, fulfillmentData, deliveryInfo, loading: false});
      } catch (error) {
        dispatch({type: DELIVERY_INFO_ERROR, error, loading: false});
      }
    };
  }

  async function getDeliveryExpectations(deliveryMethodId, listingId, lang, shstore) {
    return await get({
      host: process.env.REACT_APP_API_HOST,
      path: `/fulfillment/delivery/v1/listing/${listingId}/deliveryexpectation`,
      query: {
        shstore,
        deliveryMethodId,
      },
      headers: {
        'Accept-Language': lang,
        Authorization: process.env.REACT_APP_API_SECRET,
      },
      json: true,
    });
  }

  async function getFulFillmentDetail(listingId, lang, shstore) {
    return await get({
      host: process.env.REACT_APP_API_HOST,
      path: `/fulfillment/window/v1/listing/${listingId}`,
      query: {
        shstore,
      },
      headers: {
        'Accept-Language': lang,
        Authorization: process.env.REACT_APP_API_SECRET,
      },
      json: true,
    });
  }

  return controller;
}

export default controllerFactory;
